import { reactive, ref } from 'vue';

// Grip-handle drag-to-reorder for the studio's sidebar rails. Reordering is
// keyed: an item is identified by (key, index) and may only be dropped onto a
// sibling sharing the same key. Flat rails (EditorSideList) pass a single
// constant key; sectioned rails (SegmentedSideList) pass the section key so
// drags stay within their section.
//
// Only drags started from the grip count: `dragArmed` is set on the handle's
// mousedown and a dragstart that isn't armed is cancelled.
export function useDragReorder<K>(onReorder: (key: K, from: number, to: number) => void) {
  const dragArmed = ref(false);
  const drag = reactive<{ key: K | null; index: number | null }>({ key: null, index: null });
  const over = reactive<{ key: K | null; index: number | null }>({ key: null, index: null });

  function onDragStart(key: K, index: number, e: DragEvent) {
    if (!dragArmed.value) {
      e.preventDefault();
      return;
    }
    drag.key = key;
    drag.index = index;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      // Firefox requires data to be set for a drag to begin.
      e.dataTransfer.setData('text/plain', String(index));
    }
  }

  function onDragOver(key: K, index: number) {
    if (drag.index === null || drag.key !== key) return; // only within the same section
    over.key = key;
    over.index = index;
  }

  function onDrop(key: K, index: number) {
    if (drag.key === key && drag.index !== null && drag.index !== index) {
      onReorder(key, drag.index, index);
    }
    onDragEnd();
  }

  function onDragEnd() {
    dragArmed.value = false;
    drag.key = null;
    drag.index = null;
    over.key = null;
    over.index = null;
  }

  return { dragArmed, drag, over, onDragStart, onDragOver, onDrop, onDragEnd };
}
